import React, { useEffect, useState } from 'react';

import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';
import { supabase } from '../../../lib/supabase';

const DatabaseManager = () => {
	const [loading, setLoading] = useState(false);
	const [tables, setTables] = useState([]);
	const [selectedTable, setSelectedTable] = useState(null);
	const [tableData, setTableData] = useState([]);
	const [tableSchema, setTableSchema] = useState([]);
	const [error, setError] = useState(null);
	const [success, setSuccess] = useState(null);

	useEffect(() => {
		loadTables();
	}, []);

	const loadTables = async () => {
		try {
			setLoading(true);
			// Get list of tables from Supabase
			const { data, error } = await supabase
				.from('information_schema.tables')
				.select('table_name')
				.eq('table_schema', 'public')
				.order('table_name');

			if (error) {
				setError('Failed to load tables: ' + error.message);
			} else {
				setTables(data?.map((table) => table.table_name) || []);
			}
		} catch (error) {
			setError('Failed to load tables: ' + error.message);
		} finally {
			setLoading(false);
		}
	};

	const loadTableData = async (tableName) => {
		try {
			setLoading(true);
			setSelectedTable(tableName);

			// Load table data
			const { data, error } = await supabase.from(tableName).select('*').limit(100);

			if (error) {
				setError('Failed to load table data: ' + error.message);
			} else {
				setTableData(data || []);
			}

			// Load table schema
			const { data: schemaData, error: schemaError } = await supabase
				.from('information_schema.columns')
				.select('column_name, data_type, is_nullable, column_default')
				.eq('table_name', tableName)
				.eq('table_schema', 'public')
				.order('ordinal_position');

			if (schemaError) {
				console.error('Failed to load schema:', schemaError);
			} else {
				setTableSchema(schemaData || []);
			}
		} catch (error) {
			setError('Failed to load table data: ' + error.message);
		} finally {
			setLoading(false);
		}
	};

	const handleBackup = async () => {
		try {
			setLoading(true);
			setError(null);

			// This would trigger a backup process
			// In a real implementation, this would call a backend service
			alert(
				'Backup functionality would be implemented here. This would export all data to a JSON file.'
			);

			setSuccess('Backup process initiated successfully');
		} catch (error) {
			setError('Backup failed: ' + error.message);
		} finally {
			setLoading(false);
		}
	};

	const handleRestore = () => {
		// This would open a file picker for restore
		alert(
			'Restore functionality would be implemented here. This would allow uploading a backup file.'
		);
	};

	const handleRunMigration = () => {
		// This would show available migrations and allow running them
		alert(
			'Migration runner would be implemented here. This would show available SQL migrations and allow running them.'
		);
	};

	const handleClearCache = async () => {
		try {
			setLoading(true);
			setError(null);

			// Clear browser cache and reload
			if ('caches' in window) {
				const cacheNames = await caches.keys();
				await Promise.all(cacheNames.map((cacheName) => caches.delete(cacheName)));
			}

			setSuccess('Cache cleared successfully');
			setTimeout(() => {
				window.location.reload();
			}, 1000);
		} catch (error) {
			setError('Failed to clear cache: ' + error.message);
		} finally {
			setLoading(false);
		}
	};

	const exportTableData = async (tableName) => {
		try {
			const { data, error } = await supabase.from(tableName).select('*');

			if (error) {
				setError('Failed to export data: ' + error.message);
				return;
			}

			// Create and download JSON file
			const jsonString = JSON.stringify(data, null, 2);
			const blob = new Blob([jsonString], { type: 'application/json' });
			const url = URL.createObjectURL(blob);
			const link = document.createElement('a');
			link.href = url;
			link.download = `${tableName}_export_${new Date().toISOString().split('T')[0]}.json`;
			document.body.appendChild(link);
			link.click();
			document.body.removeChild(link);
			URL.revokeObjectURL(url);

			setSuccess(`Table ${tableName} exported successfully`);
		} catch (error) {
			setError('Export failed: ' + error.message);
		}
	};

	return (
		<div className='space-y-6'>
			{/* Header */}
			<div className='bg-white shadow rounded-lg p-6'>
				<div className='flex justify-between items-center'>
					<div>
						<h1 className='text-2xl font-bold text-gray-900'>Database Management</h1>
						<p className='text-gray-600'>Manage your database, run migrations, and backup data</p>
					</div>
					<div className='flex space-x-3'>
						<Button
							variant='outline'
							onClick={handleBackup}
							disabled={loading}
							iconName='Download'
							iconPosition='left'
						>
							Backup All Data
						</Button>
						<Button
							variant='outline'
							onClick={handleRestore}
							disabled={loading}
							iconName='Upload'
							iconPosition='left'
						>
							Restore Data
						</Button>
					</div>
				</div>
			</div>

			{/* Alerts */}
			{error && (
				<div className='bg-red-50 border border-red-200 rounded-lg p-4'>
					<div className='flex items-center'>
						<Icon name='AlertCircle' className='h-5 w-5 text-red-500 mr-2' />
						<p className='text-red-700'>{error}</p>
					</div>
				</div>
			)}

			{success && (
				<div className='bg-green-50 border border-green-200 rounded-lg p-4'>
					<div className='flex items-center'>
						<Icon name='CheckCircle' className='h-5 w-5 text-green-500 mr-2' />
						<p className='text-green-700'>{success}</p>
					</div>
				</div>
			)}

			{/* Database Tools */}
			<div className='grid grid-cols-1 md:grid-cols-3 gap-6'>
				<div className='bg-white shadow rounded-lg p-6'>
					<div className='flex items-center mb-4'>
						<div className='w-10 h-10 bg-blue-500 rounded-lg flex items-center justify-center mr-3'>
							<Icon name='Database' className='h-6 w-6 text-white' />
						</div>
						<h3 className='text-lg font-semibold text-gray-900'>Migration Runner</h3>
					</div>
					<p className='text-gray-600 mb-4'>Run database migrations and schema updates</p>
					<Button
						variant='default'
						onClick={handleRunMigration}
						className='w-full bg-blue-500 hover:bg-blue-600'
						iconName='Play'
						iconPosition='left'
					>
						Run Migrations
					</Button>
				</div>

				<div className='bg-white shadow rounded-lg p-6'>
					<div className='flex items-center mb-4'>
						<div className='w-10 h-10 bg-green-500 rounded-lg flex items-center justify-center mr-3'>
							<Icon name='RefreshCw' className='h-6 w-6 text-white' />
						</div>
						<h3 className='text-lg font-semibold text-gray-900'>Clear Cache</h3>
					</div>
					<p className='text-gray-600 mb-4'>Clear browser cache and refresh data</p>
					<Button
						variant='default'
						onClick={handleClearCache}
						className='w-full bg-green-500 hover:bg-green-600'
						iconName='Trash2'
						iconPosition='left'
					>
						Clear Cache
					</Button>
				</div>

				<div className='bg-white shadow rounded-lg p-6'>
					<div className='flex items-center mb-4'>
						<div className='w-10 h-10 bg-purple-500 rounded-lg flex items-center justify-center mr-3'>
							<Icon name='BarChart' className='h-6 w-6 text-white' />
						</div>
						<h3 className='text-lg font-semibold text-gray-900'>Database Stats</h3>
					</div>
					<p className='text-gray-600 mb-4'>View database statistics and performance</p>
					<Button
						variant='default'
						onClick={loadTables}
						className='w-full bg-purple-500 hover:bg-purple-600'
						iconName='BarChart'
						iconPosition='left'
					>
						View Stats
					</Button>
				</div>
			</div>

			{/* Tables List */}
			<div className='bg-white shadow rounded-lg p-6'>
				<h2 className='text-lg font-semibold text-gray-900 mb-4'>Database Tables</h2>
				{loading ? (
					<div className='flex items-center justify-center py-8'>
						<Icon name='Loader' className='h-6 w-6 animate-spin text-[#800020] mr-2' />
						<span className='text-gray-600'>Loading tables...</span>
					</div>
				) : (
					<div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4'>
						{tables.map((table) => (
							<div
								key={table}
								className={`p-4 border rounded-lg cursor-pointer transition-all ${
									selectedTable === table
										? 'border-[#800020] bg-[#800020]/5'
										: 'border-gray-200 hover:border-[#800020]/50'
								}`}
								onClick={() => loadTableData(table)}
							>
								<div className='flex items-center justify-between'>
									<div>
										<h3 className='font-medium text-gray-900'>{table}</h3>
										<p className='text-sm text-gray-500'>Click to view data</p>
									</div>
									<Icon name='ChevronRight' className='h-4 w-4 text-gray-400' />
								</div>
							</div>
						))}
					</div>
				)}
			</div>

			{/* Table Data Viewer */}
			{selectedTable && (
				<div className='bg-white shadow rounded-lg p-6'>
					<div className='flex justify-between items-center mb-4'>
						<h2 className='text-lg font-semibold text-gray-900'>Table: {selectedTable}</h2>
						<Button
							variant='outline'
							onClick={() => exportTableData(selectedTable)}
							iconName='Download'
							iconPosition='left'
						>
							Export Data
						</Button>
					</div>

					{/* Schema */}
					<div className='mb-6'>
						<h3 className='text-md font-medium text-gray-900 mb-3'>Schema</h3>
						<div className='overflow-x-auto'>
							<table className='min-w-full divide-y divide-gray-200'>
								<thead className='bg-gray-50'>
									<tr>
										<th className='px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
											Column
										</th>
										<th className='px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
											Type
										</th>
										<th className='px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
											Nullable
										</th>
										<th className='px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
											Default
										</th>
									</tr>
								</thead>
								<tbody className='bg-white divide-y divide-gray-200'>
									{tableSchema.map((column, index) => (
										<tr key={index}>
											<td className='px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900'>
												{column.column_name}
											</td>
											<td className='px-6 py-4 whitespace-nowrap text-sm text-gray-500'>
												{column.data_type}
											</td>
											<td className='px-6 py-4 whitespace-nowrap text-sm text-gray-500'>
												{column.is_nullable === 'YES' ? 'Yes' : 'No'}
											</td>
											<td className='px-6 py-4 whitespace-nowrap text-sm text-gray-500'>
												{column.column_default || '-'}
											</td>
										</tr>
									))}
								</tbody>
							</table>
						</div>
					</div>

					{/* Data */}
					<div>
						<h3 className='text-md font-medium text-gray-900 mb-3'>Data (First 100 rows)</h3>
						<div className='overflow-x-auto'>
							{tableData.length > 0 ? (
								<table className='min-w-full divide-y divide-gray-200'>
									<thead className='bg-gray-50'>
										<tr>
											{Object.keys(tableData[0]).map((key) => (
												<th
													key={key}
													className='px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'
												>
													{key}
												</th>
											))}
										</tr>
									</thead>
									<tbody className='bg-white divide-y divide-gray-200'>
										{tableData.map((row, index) => (
											<tr key={index}>
												{Object.values(row).map((value, cellIndex) => (
													<td
														key={cellIndex}
														className='px-6 py-4 whitespace-nowrap text-sm text-gray-500'
													>
														{typeof value === 'object' ? JSON.stringify(value) : String(value)}
													</td>
												))}
											</tr>
										))}
									</tbody>
								</table>
							) : (
								<p className='text-gray-500 text-center py-8'>No data found in this table</p>
							)}
						</div>
					</div>
				</div>
			)}
		</div>
	);
};

export default DatabaseManager;

